import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Search, Plus, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useLang } from "../lib/i18n";
import { StatusBadge } from "../components/badges";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";

const EMPTY = {
  completed_summary: "", next_summary: "", completed_issue_keys: [], planned_issue_keys: [],
  blocker_flag: false, blocker_details: "", blocker_since: "", blocker_impact: "",
  need_help_level: "NO", help_category: "", help_details: "", confidence: "",
  dependencies: [], milestone_date: "", milestone_impact: "", additional_context: "",
  restricted_note: "", edit_reason: "",
};

const newDepKey = () => `dep-${Math.random().toString(36).slice(2)}`;

const stripDepKeys = (f) => ({
  ...f,
  dependencies: (f.dependencies || []).map(({ _key, ...d }) => d),
});

function IssuePicker({ label, issues, selected, onToggle, testPrefix }) {
  const { t } = useLang();
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="flex flex-wrap gap-1.5">
        {issues.map((i) => {
          const on = selected.includes(i.issue_key);
          return (
            <button
              key={i.issue_key}
              type="button"
              data-testid={`${testPrefix}-issue-${i.issue_key}`}
              onClick={() => onToggle(i.issue_key)}
              className={`text-xs border rounded-md px-2 py-1 transition-colors ${
                on ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "hover:border-[#1E3A8A]/50"
              }`}
              title={`${i.summary} — ${i.status} (${i.priority})`}
            >
              {i.issue_key} <span className={on ? "text-blue-200" : "text-muted-foreground"}>· {i.status}</span>
            </button>
          );
        })}
        {issues.length === 0 && <span className="text-xs text-muted-foreground">{t("noData")}</span>}
      </div>
    </div>
  );
}

export default function UpdateForm() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { t } = useLang();
  const [card, setCard] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [updateId, setUpdateId] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [issues, setIssues] = useState([]);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const dirtyRef = useRef(false);
  const formRef = useRef(form);
  formRef.current = form;

  useEffect(() => {
    api.get("/me/update-queue").then((r) => {
      const c = r.data.cards.find((x) => x.project.id === projectId);
      if (!c) {
        toast.error("Project not found in your queue");
        navigate("/my-daily");
        return;
      }
      setCard(c);
      setIssues(c.suggested_issues || []);
      if (c.update) {
        setUpdateId(c.update.id);
        setIsSubmitted(c.update.status === "SUBMITTED");
        const loaded = { ...EMPTY, ...Object.fromEntries(Object.entries(c.update).filter(([k, v]) => k in EMPTY && v !== null)) };
        loaded.dependencies = (loaded.dependencies || []).map((d) => ({ ...d, _key: newDepKey() }));
        setForm(loaded);
      }
    });
  }, [projectId, navigate]);

  const saveDraft = useCallback(async (silent = true) => {
    if (isSubmitted) return null;
    setSaving(true);
    try {
      const f = stripDepKeys(formRef.current);
      const { data } = await api.post(`/projects/${projectId}/updates`, {
        ...f,
        blocker_since: f.blocker_since || null,
        milestone_date: f.milestone_date || null,
      });
      setUpdateId(data.id);
      dirtyRef.current = false;
      if (!silent) toast.success(t("draftSaved"));
      return data.id;
    } catch (e) {
      if (!silent) toast.error(apiError(e));
      return null;
    } finally {
      setSaving(false);
    }
  }, [projectId, isSubmitted, t]);

  useEffect(() => {
    const iv = setInterval(() => {
      if (dirtyRef.current && !isSubmitted) saveDraft(true);
    }, 2500);
    return () => clearInterval(iv);
  }, [saveDraft, isSubmitted]);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    dirtyRef.current = true;
  };

  const doSearch = async (q) => {
    setSearch(q);
    try {
      const { data } = await api.get(`/jira/search`, { params: { project_id: projectId, q } });
      setIssues(q ? data.issues : card?.suggested_issues || data.issues);
    } catch (e) { /* noop */ }
  };

  const toggleKey = (field) => (key) =>
    set(field, form[field].includes(key) ? form[field].filter((k) => k !== key) : [...form[field], key]);

  const submit = async () => {
    setSubmitting(true);
    try {
      let id = updateId;
      if (isSubmitted) {
        await api.patch(`/updates/${id}`, stripDepKeys(formRef.current));
        toast.success(t("submitted") + " ✓");
        navigate("/my-daily");
        return;
      }
      id = await saveDraft(true);
      if (!id) throw new Error("Could not save draft");
      await api.post(`/updates/${id}/submit`);
      toast.success(t("submitted") + " ✓");
      navigate("/my-daily");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (!card)
    return <div className="space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-60 w-full" /></div>;

  return (
    <div className="max-w-3xl mx-auto pb-24" data-testid="update-form-page">
      <button onClick={() => navigate("/my-daily")} data-testid="update-back-btn"
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4">
        <ArrowLeft className="h-4 w-4" /> {t("myDaily")}
      </button>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-6">
        <div>
          <div className="text-xs text-muted-foreground">{card.project.key} · {card.expected_report.reporting_date}</div>
          <h1 className="font-heading text-2xl font-semibold">{card.project.name}</h1>
        </div>
        <StatusBadge status={isSubmitted ? "SUBMITTED_ON_TIME" : card.status} />
      </div>

      <div className="space-y-6">
        <section className="apha-card p-5 space-y-3">
          <h2 className="font-heading font-semibold text-base">{t("whatCompleted")}</h2>
          <Textarea data-testid="update-completed-input" rows={3} value={form.completed_summary}
            onChange={(e) => set("completed_summary", e.target.value)} placeholder="..." />
          <IssuePicker label={t("relatedIssues")} issues={issues} selected={form.completed_issue_keys}
            onToggle={toggleKey("completed_issue_keys")} testPrefix="completed" />
        </section>

        <section className="apha-card p-5 space-y-3">
          <h2 className="font-heading font-semibold text-base">{t("whatToday")}</h2>
          <Textarea data-testid="update-planned-input" rows={3} value={form.next_summary}
            onChange={(e) => set("next_summary", e.target.value)} placeholder="..." />
          <div className="relative">
            <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input data-testid="jira-search-input" className="pl-8" placeholder={t("searchIssues")}
              value={search} onChange={(e) => doSearch(e.target.value)} />
          </div>
          <IssuePicker label={t("relatedIssues")} issues={issues} selected={form.planned_issue_keys}
            onToggle={toggleKey("planned_issue_keys")} testPrefix="planned" />
        </section>

        <section className="apha-card p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-semibold text-base">{t("anyBlockers")}</h2>
            <Switch data-testid="blocker-switch" checked={form.blocker_flag}
              onCheckedChange={(v) => set("blocker_flag", v)} />
          </div>
          {form.blocker_flag && (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label>{t("blockerDesc")}</Label>
                <Textarea data-testid="blocker-details-input" rows={2} value={form.blocker_details || ""}
                  onChange={(e) => set("blocker_details", e.target.value)} />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>{t("blockedSince")}</Label>
                  <Input type="date" value={(form.blocker_since || "").slice(0, 10)}
                    onChange={(e) => set("blocker_since", e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label>{t("blockerImpact")}</Label>
                  <Input data-testid="blocker-impact-input" value={form.blocker_impact || ""}
                    onChange={(e) => set("blocker_impact", e.target.value)} />
                </div>
              </div>
            </div>
          )}
        </section>

        <section className="apha-card p-5 space-y-3">
          <h2 className="font-heading font-semibold text-base">{t("needHelp")}</h2>
          <div className="flex gap-2">
            {[["NO", t("helpNo")], ["YES_SOON", t("helpSoon")], ["YES_NOW", t("helpNow")]].map(([v, label]) => (
              <button key={v} type="button" data-testid={`help-level-${v.toLowerCase()}`}
                onClick={() => set("need_help_level", v)}
                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                  form.need_help_level === v
                    ? v === "YES_NOW" ? "bg-[#991B1B] text-white border-[#991B1B]" : "bg-[#1E3A8A] text-white border-[#1E3A8A]"
                    : "hover:border-[#1E3A8A]/50"
                }`}>
                {label}
              </button>
            ))}
          </div>
          {form.need_help_level !== "NO" && (
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>{t("helpCategory")}</Label>
                <Select value={form.help_category || ""} onValueChange={(v) => set("help_category", v)}>
                  <SelectTrigger data-testid="help-category-select"><SelectValue placeholder="..." /></SelectTrigger>
                  <SelectContent>
                    {["TECHNICAL", "REQUIREMENT", "DECISION", "RESOURCE", "ENVIRONMENT", "OTHER"].map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>{t("helpDetails")}</Label>
                <Input data-testid="help-details-input" value={form.help_details || ""}
                  onChange={(e) => set("help_details", e.target.value)} />
              </div>
            </div>
          )}
        </section>

        <section className="apha-card p-5 space-y-3">
          <h2 className="font-heading font-semibold text-base">{t("confidence")}</h2>
          <div className="flex flex-wrap gap-2">
            {[["ON_TRACK", t("onTrack"), "#166534"], ["CONCERNED", t("concerned"), "#92400E"],
              ["AT_RISK", t("atRisk"), "#9A3412"], ["UNKNOWN", t("unknown"), "#334155"]].map(([v, label, color]) => (
              <button key={v} type="button" data-testid={`confidence-${v.toLowerCase()}`}
                onClick={() => set("confidence", v)}
                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                  form.confidence === v ? "text-white" : "hover:border-[#1E3A8A]/50"
                }`}
                style={form.confidence === v ? { backgroundColor: color, borderColor: color } : {}}>
                {label}
              </button>
            ))}
          </div>
        </section>

        <section className="apha-card p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-semibold text-base">{t("dependencies")}</h2>
            <Button type="button" variant="outline" size="sm" data-testid="add-dependency-btn"
              onClick={() => set("dependencies", [...form.dependencies, { _key: newDepKey(), description: "", dependency_type: "INTERNAL", owner_name: "", needed_by: "" }])}>
              <Plus className="h-3.5 w-3.5 mr-1" /> {t("addDependency")}
            </Button>
          </div>
          {form.dependencies.map((d, idx) => (
            <div key={d._key || `dep-${idx}`} className="border rounded-md p-3 space-y-2 relative" data-testid={`dep-row-${idx}`}>
              <button type="button" data-testid={`remove-dep-${idx}`} className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
                onClick={() => set("dependencies", form.dependencies.filter((_, i) => i !== idx))}>
                <X className="h-4 w-4" />
              </button>
              <Input placeholder={t("depDescription")} data-testid={`dep-desc-${idx}`} value={d.description}
                onChange={(e) => set("dependencies", form.dependencies.map((x, i) => i === idx ? { ...x, description: e.target.value } : x))} />
              <div className="grid grid-cols-3 gap-2">
                <Select value={d.dependency_type}
                  onValueChange={(v) => set("dependencies", form.dependencies.map((x, i) => i === idx ? { ...x, dependency_type: v } : x))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="INTERNAL">Internal</SelectItem>
                    <SelectItem value="EXTERNAL">External</SelectItem>
                    <SelectItem value="DECISION">Decision</SelectItem>
                  </SelectContent>
                </Select>
                <Input placeholder={t("depOwner")} value={d.owner_name || ""}
                  onChange={(e) => set("dependencies", form.dependencies.map((x, i) => i === idx ? { ...x, owner_name: e.target.value } : x))} />
                <Input type="date" value={d.needed_by || ""}
                  onChange={(e) => set("dependencies", form.dependencies.map((x, i) => i === idx ? { ...x, needed_by: e.target.value } : x))} />
              </div>
            </div>
          ))}
        </section>

        <section className="apha-card p-5 space-y-3">
          <h2 className="font-heading font-semibold text-base">{t("milestone")}</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <Input type="date" data-testid="milestone-date-input" value={form.milestone_date || ""}
              onChange={(e) => set("milestone_date", e.target.value)} />
            <Input placeholder={t("blockerImpact")} value={form.milestone_impact || ""}
              onChange={(e) => set("milestone_impact", e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>{t("additionalContext")}</Label>
            <Textarea rows={2} value={form.additional_context || ""}
              onChange={(e) => set("additional_context", e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>{t("restrictedNote")}</Label>
            <Textarea data-testid="restricted-note-input" rows={2} value={form.restricted_note || ""}
              onChange={(e) => set("restricted_note", e.target.value)} />
          </div>
          {isSubmitted && (
            <div className="space-y-1">
              <Label>{t("editReason")}</Label>
              <Input data-testid="edit-reason-input" value={form.edit_reason || ""}
                onChange={(e) => set("edit_reason", e.target.value)} />
            </div>
          )}
        </section>
      </div>

      <div className="fixed bottom-0 left-0 right-0 lg:left-60 bg-white/90 backdrop-blur-xl border-t p-3 z-30">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <span className="text-xs text-muted-foreground flex-1">
            {saving ? "Saving..." : dirtyRef.current ? "Unsaved changes" : t("draftSaved")}
          </span>
          {!isSubmitted && (
            <Button variant="outline" data-testid="save-draft-btn" onClick={() => saveDraft(false)}>
              {t("saveDraft")}
            </Button>
          )}
          <Button data-testid="submit-update-btn" className="bg-[#1E3A8A] hover:bg-[#1c3479]"
            disabled={submitting} onClick={submit}>
            {submitting ? "..." : t("submitUpdate")}
          </Button>
        </div>
      </div>
    </div>
  );
}
